"use client";

import { Bot, MessageSquare, Sparkles } from "lucide-react";
import { useStore } from "@/lib/store";
import type { AgentRun } from "@/lib/types";
import { Markdown } from "@/components/ui/Markdown";
import { formatRelativeTime } from "@/lib/timezone";

/**
 * One agent run = one (user prompt → agent actions) cycle.
 * Header shows the bot identity + session name + timestamp.
 * Body shows the prompt (quoted) and the action bullets, rendered through
 * <Markdown> so [[wikilinks]], @ABC-123, and #tags become navigable.
 */
export function AgentRunCard({ run }: { run: AgentRun }) {
  const session = useStore((s) =>
    s.agentSessions.find((x) => x.id === run.sessionId)
  );
  const actor = useStore((s) => s.users.find((u) => u.id === run.actorId));
  const isBot = !!(actor?.email?.startsWith("bot+") || actor?.email?.endsWith("@bots.pauta.dev"));

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--border-strong)] transition-colors">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--border)]/60">
        <span
          className="w-5 h-5 rounded flex items-center justify-center shrink-0 text-[10px]"
          style={{ background: actor?.color ?? "var(--bg-tertiary)" }}
        >
          {isBot ? "🤖" : actor?.initials ?? "?"}
        </span>
        <span className="text-[12px] font-medium text-[var(--fg)] truncate">
          {actor?.name ?? "Unknown"}
        </span>
        {isBot && (
          <span className="inline-flex items-center gap-0.5 px-1 rounded text-[9px] font-semibold uppercase tracking-wide bg-[var(--accent-bg)] text-[var(--accent)]">
            <Bot size={9} /> agent
          </span>
        )}
        <span className="text-[11px] text-[var(--fg-muted)]">·</span>
        <span className="text-[11.5px] text-[var(--fg-secondary)] flex items-center gap-1 truncate">
          <Sparkles size={10} className="text-[var(--accent)] shrink-0" />
          <span className="truncate" title={session?.name}>
            {session?.name ?? "Session not loaded"}
          </span>
        </span>
        <span className="ml-auto text-[10.5px] text-[var(--fg-muted)] shrink-0">
          {formatRelativeTime(run.createdAt)}
        </span>
      </div>

      {/* Prompt — quoted */}
      <div className="px-3 pt-3 pb-2 border-l-2 border-[var(--accent)]/40 ml-2 mr-3 mt-2">
        <div className="flex items-start gap-1.5">
          <MessageSquare size={11} className="text-[var(--fg-muted)] mt-1 shrink-0" />
          <div className="flex-1 min-w-0 text-[12.5px] text-[var(--fg-secondary)] italic leading-snug">
            {run.prompt}
          </div>
        </div>
      </div>

      {/* Summary (1–3 sentences) */}
      {run.summary?.trim() && (
        <div className="px-4 pt-1 pb-2 text-[12.5px] text-[var(--fg)] leading-relaxed prose-doc">
          <Markdown content={run.summary} />
        </div>
      )}

      {/* Action bullets */}
      {run.actions.length > 0 && (
        <div className="px-4 pb-3">
          <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--fg-muted)] mb-1">
            Actions ({run.actions.length})
          </div>
          <ul className="space-y-0.5 text-[12px] text-[var(--fg-secondary)]">
            {run.actions.map((a, i) => (
              <li key={i} className="flex items-start gap-1.5 prose-doc">
                <span className="text-[var(--fg-muted)] mt-1.5">•</span>
                <span className="flex-1 min-w-0">
                  <Markdown content={a} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
